'use client'

import { useEffect, useRef, useState } from 'react'
import { usePathname, useSearchParams, useRouter } from 'next/navigation'
import { useAuthContext } from '@/app/firebase/AuthContext'

const PUBLIC_PATHS = ['/login', '/oauth-callback']

const AI_OVERVIEW_ANALYSIS_PREFIX = '/ai-overview-analysis/'

function getReportIdFromPath(pathname: string): string | null {
  if (!pathname.startsWith(AI_OVERVIEW_ANALYSIS_PREFIX)) return null
  const rest = pathname.slice(AI_OVERVIEW_ANALYSIS_PREFIX.length)
  const segments = rest.split('/').filter(Boolean)
  if (segments.length === 0) return null
  return segments[0]
}

export function AuthGate({ children }: { children: React.ReactNode }) {
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const router = useRouter()
  const { authStatus, loading } = useAuthContext()
  const [reportPublicCheck, setReportPublicCheck] = useState<'idle' | 'allowed' | 'denied'>('idle')
  const reportCheckIdRef = useRef<string | null>(null)

  useEffect(() => {
    if (loading) return

    if (authStatus === 'authorised') {
      setReportPublicCheck('idle')
      reportCheckIdRef.current = null
      return
    }

    if (PUBLIC_PATHS.includes(pathname)) {
      setReportPublicCheck('idle')
      return
    }

    const reportId = getReportIdFromPath(pathname)
    if (reportId) {
      if (reportCheckIdRef.current === reportId && reportPublicCheck === 'allowed') return
      reportCheckIdRef.current = reportId
      setReportPublicCheck('idle')

      const apiBase = process.env.NEXT_PUBLIC_API_URL?.replace(/\/$/, '') || ''
      if (!apiBase) {
        setReportPublicCheck('denied')
        return
      }
      fetch(`${apiBase}/report-public?reportId=${encodeURIComponent(reportId)}`)
        .then((res) => res.json())
        .then((body: { public?: boolean }) => {
          if (body?.public === true) {
            setReportPublicCheck('allowed')
          } else {
            setReportPublicCheck('denied')
          }
        })
        .catch((err) => {
          console.error('Error fetching report public status:', err)
          setReportPublicCheck('denied')
        })
      return
    }

    const next = `${pathname}${searchParams.toString() ? `?${searchParams.toString()}` : ''}`
    router.replace(`/login?next=${encodeURIComponent(next)}`)
  }, [loading, authStatus, pathname, searchParams, router, reportPublicCheck])

  useEffect(() => {
    if (loading || authStatus === 'authorised') return
    if (PUBLIC_PATHS.includes(pathname)) return

    const reportId = getReportIdFromPath(pathname)
    if (reportId) {
      if (reportPublicCheck === 'denied') {
        const next = `${pathname}${searchParams.toString() ? `?${searchParams.toString()}` : ''}`
        router.replace(`/login?next=${encodeURIComponent(next)}`)
      }
      return
    }
  }, [loading, authStatus, pathname, searchParams, router, reportPublicCheck])

  return <>{children}</>
}
